import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import HitlBadge from "@/components/hitl/HitlBadge";
import HitlAssignMenu from "@/components/hitl/HitlAssignMenu";
import HitlActionPrompt from "@/components/hitl/HitlActionPrompt";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { useHitlItem } from "@/hooks/useHitlItem";
import { useHitlActions } from "@/hooks/useHitlActions";
import { useHasCapability } from "@/hooks/useCapabilities";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";
import type { HitlItem } from "@/types/hitl";
import type { MemoryEpisode } from "@/api/memory";
import type { TimelineEvent } from "@/api/observability";
import type { CrmContact } from "@/types/crm";
import { updateContact } from "@/api/crm";
import { useToast } from "@/components/ToastProvider";

interface HitlDetailDrawerProps {
  open: boolean;
  hitlId?: string | null;
  item?: HitlItem | null;
  onClose: () => void;
  onActionComplete?: () => void;
  defaultCategory?: string | null;
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const next = new Date(value);
  if (Number.isNaN(next.getTime())) return value;
  return next.toLocaleString();
}

function coerceMetadataId(value: unknown): string | null {
  if (typeof value === "number") return String(value);
  if (typeof value === "string" && value.trim().length) return value;
  return null;
}

function buildLinkParams(item: HitlItem | null, hitlId?: string | null, defaultCategory?: string | null) {
  const params = new URLSearchParams();
  if (hitlId) params.set("open", hitlId);
  if (item?.category ?? defaultCategory) {
    const id = (item?.category ?? defaultCategory ?? "").toString();
    if (id && id !== "all") params.set("category", id);
  }
  if (item?.thread_id) {
    params.set("thread_id", item.thread_id);
  }
  if (item?.assigned_to_me) {
    params.set("mine", "1");
  }
  return params;
}

function TimelineList({ events = [] }: { events?: TimelineEvent[] }) {
  if (!events?.length) {
    return <p className="text-sm text-muted-foreground">No timeline events captured yet.</p>;
  }
  const trimmed = events.slice(0, 6);
  return (
    <ol className="space-y-3">
      {trimmed.map((event, index) => (
        <li key={`${event.timestamp}-${index}`} className="border-l border-border pl-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{event.event_type ?? "event"}</div>
          <p className="text-sm text-foreground">{event.summary ?? "—"}</p>
          <p className="text-xs text-muted-foreground">{formatDate(event.timestamp)}</p>
        </li>
      ))}
    </ol>
  );
}

function MemoryList({ episodes = [] }: { episodes?: MemoryEpisode[] }) {
  if (!episodes?.length) {
    return <p className="text-sm text-muted-foreground">No memory episodes linked.</p>;
  }
  return (
    <div className="space-y-3">
      {episodes.slice(0, 4).map((episode) => (
        <article key={episode.id} className="rounded-xl border border-border bg-background/60 p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{episode.agent}</div>
          <p className="text-sm text-foreground">{episode.summary}</p>
          <p className="text-xs text-muted-foreground">{formatDate(episode.timestamp)}</p>
        </article>
      ))}
    </div>
  );
}

export function HitlDetailDrawer({ open, hitlId, item: fallbackItem, onClose, onActionComplete, defaultCategory }: HitlDetailDrawerProps) {
  const { item, related, loading, error, refresh } = useHitlItem(hitlId);
  const displayItem = item ?? fallbackItem ?? null;
  const actions = useHitlActions(hitlId);
  const [prompt, setPrompt] = useState<"reject" | "request_changes" | null>(null);
  const [notes, setNotes] = useState("");
  const { push } = useToast();
  const { openUpgradeModal } = useUpgradeModal();
  const canAssign = useHasCapability("canAssignHitl");
  const canApprove = useHasCapability("canApproveHitl");
  const canReject = useHasCapability("canRejectHitl");
  const canResolve = useHasCapability("canResolveHitlItem");
  const canOpenDrawer = useHasCapability("canOpenHitlDrawer");
  const canUseContext = useHasCapability("canUseHitlContext");
  const canUseNarratives = useHasCapability("canUseHitlNarratives");

  const params = useMemo(() => buildLinkParams(displayItem, hitlId, defaultCategory), [defaultCategory, displayItem, hitlId]);
  const approvalsLink = `/agents/observability${params.toString() ? `?${params.toString()}` : ""}`;
  const inboxLink = `/hitl/inbox${params.toString() ? `?${params.toString()}` : ""}`;
  const observabilityLink = `/agents/observability${params.toString() ? `?${params.toString()}` : ""}`;
  const metadata = (displayItem?.metadata ?? {}) as Record<string, unknown>;
  const leadId = coerceMetadataId(metadata.lead_id ?? metadata.crm_lead_id ?? (related.lead as { id?: string | number })?.id);
  const contactId = coerceMetadataId(metadata.contact_id ?? metadata.crm_contact_id ?? (related.contact as { id?: string | number })?.id);
  const eventId = coerceMetadataId(metadata.crm_event_id ?? metadata.event_id ?? (related.event as { id?: string | number })?.id);
  const venueId = coerceMetadataId(metadata.venue_id ?? (related.venue as { id?: string | number })?.id);

  const metadataLinks = useMemo(() => {
    const links: Array<{ label: string; href: string }> = [];
    if (leadId) {
      links.push({
        label: related.lead && "title" in related.lead ? `Lead: ${String((related.lead as { title?: string }).title ?? leadId)}` : `Lead ${leadId}`,
        href: `/contacts/leads/${leadId}`,
      });
    }
    if (contactId) {
      links.push({
        label: related.contact && "name" in related.contact ? `Contact: ${String((related.contact as { name?: string }).name ?? contactId)}` : `Contact ${contactId}`,
        href: `/contacts?contact=${contactId}`,
      });
    }
    if (eventId) {
      links.push({
        label: related.event && "title" in related.event ? `Event: ${String((related.event as { title?: string }).title ?? eventId)}` : `Event ${eventId}`,
        href: `/contacts/events/${eventId}`,
      });
    }
    if (venueId) {
      links.push({
        label: related.venue && "venue" in related.venue ? `Venue: ${String((related.venue as { venue?: string }).venue ?? venueId)}` : `Venue ${venueId}`,
        href: `/contacts/venues/${venueId}`,
      });
    }
    const memoryParams = new URLSearchParams();
    if (leadId) memoryParams.set("lead_id", leadId);
    if (eventId) memoryParams.set("event_id", eventId);
    if (venueId) memoryParams.set("venue_id", venueId);
    if (memoryParams.toString()) {
      links.push({
        label: "Memory Episodes",
        href: `/memory?tab=episodes&${memoryParams.toString()}`,
      });
    }
    return links;
  }, [eventId, leadId, related.contact, related.event, related.lead, related.venue, venueId, contactId]);

  const [contactForm, setContactForm] = useState<Partial<CrmContact>>({});
  const [contactSaving, setContactSaving] = useState(false);

  useEffect(() => {
    if (!related.contact) return;
    setContactForm({
      name: related.contact.name ?? "",
      contact_name: related.contact.contact_name ?? "",
      company_name: related.contact.company_name ?? "",
      email: related.contact.email ?? "",
      phone: related.contact.phone ?? "",
      website: related.contact.website ?? "",
      location_city: related.contact.location_city ?? "",
      location_region: related.contact.location_region ?? "",
      country: related.contact.country ?? "",
      address_line: related.contact.address_line ?? "",
      notes: related.contact.notes ?? "",
    });
  }, [related.contact?.id]);

  if (!open) return null;

  const handleActionComplete = () => {
    onActionComplete?.();
    void refresh();
  };

  const handleAssign = async (value: string | null | undefined) => {
    if (!canAssign) {
      openUpgradeModal("Assign HITL");
      return;
    }
    try {
      await actions.assignTo(value);
      handleActionComplete();
    } catch (error: any) {
      if (error?.error === "requires_pro") {
        openUpgradeModal("Assign HITL");
      }
    }
  };

  const handleApprove = async () => {
    if (!canApprove) {
      openUpgradeModal("Approve HITL");
      return;
    }
    try {
      await actions.approve();
      handleActionComplete();
    } catch (error: any) {
      if (error?.error === "requires_pro") {
        openUpgradeModal("Approve HITL");
      }
    }
  };

  const handleSaveContact = async () => {
    const numericId = contactId && !Number.isNaN(Number(contactId)) ? Number(contactId) : null;
    if (!numericId) return;
    setContactSaving(true);
    try {
      await updateContact(numericId, contactForm);
      push({ title: "Contact updated", message: "Contact details saved." });
      void refresh();
    } catch (error) {
      push({ title: "Contact update failed", message: error instanceof Error ? error.message : "Unable to update contact." });
    } finally {
      setContactSaving(false);
    }
  };

  const runPromptAction = async () => {
    if (!prompt || !hitlId || !notes.trim()) return;
    if (prompt === "reject" && !canReject) {
      openUpgradeModal("Reject HITL");
      return;
    }
    if (prompt === "request_changes" && !canResolve) {
      openUpgradeModal("Resolve HITL");
      return;
    }
    try {
      if (prompt === "reject") {
        await actions.reject(notes.trim());
      } else {
        await actions.requestChanges(notes.trim());
      }
      handleActionComplete();
      setPrompt(null);
      setNotes("");
    } catch (error: any) {
      if (error?.error === "requires_pro") {
        openUpgradeModal(prompt === "reject" ? "Reject HITL" : "Resolve HITL");
      }
    }
  };

  if (!open || !canOpenDrawer) {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex">
        <div className="flex-1 bg-black/50" onClick={onClose} />
        <div className="relative h-full w-full max-w-3xl overflow-y-auto border-l border-border/5 bg-background p-6">
          <LockedFeatureCard
            title="Unlock HITL drawer"
            description="Upgrade to inspect workflow context, narratives, and take action."
            featureId="canOpenHitlDrawer"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex">
        <div className="flex-1 bg-black/50" onClick={onClose} />
        <div className="relative h-full w-full max-w-3xl overflow-y-auto border-l border-border/5 bg-background p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{displayItem?.category ?? "general"}</p>
              <h2 className="text-2xl font-semibold text-foreground">{displayItem?.title ?? displayItem?.node_key ?? "HITL detail"}</h2>
              <p className="text-sm text-muted-foreground">{displayItem?.summary ?? displayItem?.description ?? "Awaiting summary."}</p>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <HitlBadge status={displayItem?.status} deadline={displayItem?.deadline} size="md" />
            <CapabilityGate
              flag="canAssignHitl"
              fallback={
                <LockedFeatureCard
                  className="max-w-xs"
                  title="Assign reviewers"
                  description="Upgrade to delegate HITL items."
                  featureId="canAssignHitl"
                />
              }
            >
              <HitlAssignMenu
                assignedLabel={displayItem?.assigned_to_display ?? displayItem?.assigned_to}
                onAssign={handleAssign}
                busy={actions.pendingAction === "assign"}
                disabled={!canAssign}
              />
            </CapabilityGate>
            <Button type="button" variant="ghost" size="sm" onClick={() => refresh()} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
          </div>

          {error && <p className="mt-3 rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-100">{error}</p>}

          <section className="mt-6 grid gap-4 rounded-2xl border border-border bg-background/60 p-4 text-sm text-foreground/80 md:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Agent</p>
              <p className="text-sm text-foreground">{displayItem?.agent ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Workflow</p>
              <p className="text-sm">{displayItem?.workflow ?? displayItem?.workflow_id ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Thread</p>
              <p className="text-sm">{displayItem?.thread_id ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Node</p>
              <p className="text-sm">{displayItem?.node_key ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Priority</p>
              <p className="text-sm capitalize">{displayItem?.priority ?? "normal"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Created</p>
              <p className="text-sm">{formatDate(displayItem?.created_at)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Updated</p>
              <p className="text-sm">{formatDate(displayItem?.updated_at)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Created by agent</p>
              <p className="text-sm">{displayItem?.created_by_agent ?? "—"}</p>
            </div>
          </section>

          <CapabilityGate
            flag="canUseHitlContext"
            fallback={
              <LockedFeatureCard
                className="mt-6"
                title="Workflow context"
                description="Upgrade to see thread graphs, CRM links, and workflow relationships."
                featureId="canUseHitlContext"
              />
            }
          >
            <section className="mt-6 space-y-3">
              <header className="flex flex-wrap items-center gap-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Related links</h3>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Link to={approvalsLink} className="rounded-full border border-border px-3 py-1 text-primary-foreground dark:text-amber-200 hover:text-amber-50">
                    View in Observability
                  </Link>
                  <Link to={inboxLink} className="rounded-full border border-border px-3 py-1 text-foreground/70 hover:text-amber-50">
                    View Inbox
                  </Link>
                  <Link to={observabilityLink} className="rounded-full border border-border px-3 py-1 text-foreground/70 hover:text-amber-50">
                    Observability
                  </Link>
                  {displayItem?.thread_id && (
                    <Link
                      to={`/graph/thread/${encodeURIComponent(displayItem.thread_id)}`}
                      className="rounded-full border border-border px-3 py-1 text-foreground/70 hover:text-amber-50"
                    >
                      Thread graph
                    </Link>
                  )}
                  {displayItem?.workflow_id && (
                    <Link
                      to={`/graph/workflows/${encodeURIComponent(displayItem.workflow_id)}`}
                      className="rounded-full border border-border px-3 py-1 text-foreground/70 hover:text-amber-50"
                    >
                      Workflow graph
                    </Link>
                  )}
                  {metadataLinks.map((link) => (
                    <Link key={link.href} to={link.href} className="rounded-full border border-border px-3 py-1 text-foreground/70 hover:text-amber-50">
                      {link.label}
                    </Link>
                  ))}
                </div>
              </header>
            </section>
          </CapabilityGate>

          {related.contact && (
            <section className="mt-6 space-y-3 rounded-2xl border border-border bg-background/60 p-4 text-sm text-foreground/80">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Contact</p>
                  <p className="text-sm font-semibold text-foreground">
                    {related.contact.name || related.contact.company_name || `Contact ${contactId ?? ""}`}
                  </p>
                </div>
                <Button type="button" size="sm" onClick={handleSaveContact} disabled={contactSaving}>
                  {contactSaving ? "Saving…" : "Save contact"}
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs text-muted-foreground">
                  Display name
                  <input
                    type="text"
                    value={contactForm.name ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, name: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Contact name
                  <input
                    type="text"
                    value={contactForm.contact_name ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, contact_name: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Company
                  <input
                    type="text"
                    value={contactForm.company_name ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, company_name: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Email
                  <input
                    type="email"
                    value={contactForm.email ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, email: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Phone
                  <input
                    type="text"
                    value={contactForm.phone ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, phone: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Website
                  <input
                    type="text"
                    value={contactForm.website ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, website: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  City
                  <input
                    type="text"
                    value={contactForm.location_city ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, location_city: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Region
                  <input
                    type="text"
                    value={contactForm.location_region ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, location_region: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Country
                  <input
                    type="text"
                    value={contactForm.country ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, country: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Address
                  <input
                    type="text"
                    value={contactForm.address_line ?? ""}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, address_line: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  />
                </label>
              </div>
              <label className="text-xs text-muted-foreground">
                Notes
                <textarea
                  value={contactForm.notes ?? ""}
                  onChange={(event) => setContactForm((prev) => ({ ...prev, notes: event.target.value }))}
                  rows={3}
                  className="mt-1 w-full resize-none rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                />
              </label>
            </section>
          )}

          <CapabilityGate
            flag="canUseHitlContext"
            fallback={
              <LockedFeatureCard
                className="mt-6"
                title="Timeline preview"
                description="Upgrade to inspect HITL event trails and context."
                featureId="canUseHitlContext"
              />
            }
          >
            <section className="mt-6 space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Timeline</h3>
              <TimelineList events={related.threadTimeline as TimelineEvent[]} />
            </section>
            <section className="mt-6 space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Memory episodes</h3>
              <MemoryList episodes={related.memoryEpisodes as MemoryEpisode[]} />
            </section>
          </CapabilityGate>

          <CapabilityGate
            flag="canUseHitlNarratives"
            fallback={
              <LockedFeatureCard
                className="mt-8"
                title="Narratives & reasoning"
                description="Upgrade to unlock narrated reasoning and advanced insights."
                featureId="canUseHitlNarratives"
              />
            }
          >
            <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
              <CapabilityGate
                flag="canResolveHitlItem"
                fallback={
                  <LockedFeatureCard
                    title="Request revisions"
                    description="Upgrade to send changes back to agents."
                    featureId="canResolveHitlItem"
                  />
                }
              >
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (!canResolve) {
                      openUpgradeModal("Resolve HITL");
                      return;
                    }
                    setPrompt("request_changes");
                  }}
                  disabled={!canResolve || actions.pendingAction === "approve"}
                >
                  {actions.pendingAction === "request_changes" ? "Sending…" : "Request changes"}
                </Button>
              </CapabilityGate>
              <CapabilityGate
                flag="canRejectHitl"
                fallback={
                  <LockedFeatureCard
                    title="Reject HITL items"
                    description="Upgrade to decline agent actions directly."
                    featureId="canRejectHitl"
                  />
                }
              >
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (!canReject) {
                      openUpgradeModal("Reject HITL");
                      return;
                    }
                    setPrompt("reject");
                  }}
                  disabled={!canReject || actions.pendingAction === "approve"}
                >
                  {actions.pendingAction === "reject" ? "Rejecting…" : "Reject"}
                </Button>
              </CapabilityGate>
              <CapabilityGate
                flag="canApproveHitl"
                fallback={
                  <LockedFeatureCard
                    title="Approve HITL"
                    description="Upgrade to put workflows back into motion."
                    featureId="canApproveHitl"
                  />
                }
              >
                <Button type="button" onClick={handleApprove} disabled={!canApprove || (actions.pendingAction !== null && actions.pendingAction !== "approve")}>
                  {actions.pendingAction === "approve" ? "Approving…" : "Approve"}
                </Button>
              </CapabilityGate>
            </div>
            {prompt && (
              <HitlActionPrompt
                title={prompt === "reject" ? "Reject HITL item" : "Request changes"}
                placeholder={prompt === "reject" ? "Provide a short rejection note" : "Share the adjustments needed"}
                confirmLabel={prompt === "reject" ? "Reject" : "Send notes"}
                value={notes}
                onChange={setNotes}
                onCancel={() => {
                  setPrompt(null);
                  setNotes("");
                }}
                onConfirm={runPromptAction}
                busy={actions.pendingAction === prompt}
              />
            )}
          </CapabilityGate>
        </div>
      </div>
    </>
  );
}

export default HitlDetailDrawer;
